import React, { useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Button, Input, Modal, message } from 'antd'
import {
  CopyOutlined,
  DownloadOutlined,
  PrinterOutlined,
  FullscreenOutlined
} from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import { useSlerp } from '@slerp/client'
// @ts-ignore
import QRCode from 'qrcode.react'
import { GET_WALLET_PASS_QR_URL } from './LoyaltyQueries'

// Rendered as a high-resolution canvas and scaled down for display, so the
// same canvas yields a crisp PNG for both Download and Print
const QR_CANVAS_SIZE = 1024

const sanitiseSlug = (slug?: string) =>
  (slug ?? '').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()

const LoyaltyQRCard = () => {
  const { user } = useSlerp()
  const qrPreviewRef = useRef<HTMLDivElement>(null)
  const [expandVisible, setExpandVisible] = useState(false)

  const { data, loading } = useQuery(GET_WALLET_PASS_QR_URL, {
    variables: { merchantId: user?.merchant?.id ?? '' },
    skip: !user?.merchant?.id
  })

  const walletPassQrUrl: string | null | undefined = data?.walletPassQrUrl

  if (loading || !walletPassQrUrl) {
    return null
  }

  const getPreviewCanvas = () => qrPreviewRef.current?.querySelector('canvas')

  const handleCopy = () => {
    navigator.clipboard
      .writeText(walletPassQrUrl)
      .then(() => message.success('Copied'))
      .catch(() => message.error('Unable to copy the URL'))
  }

  const handleDownload = () => {
    const canvas = getPreviewCanvas()

    if (!canvas) return

    try {
      const pngDataUrl = canvas.toDataURL('image/png')
      const filename = `${sanitiseSlug(user?.merchant?.slug)}-loyalty-qr.png`

      const downloadLink = document.createElement('a')
      downloadLink.href = pngDataUrl
      downloadLink.download = filename
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    } catch (error) {
      console.error('Unable to download the loyalty QR code', error)
      message.error('Unable to download the QR code')
    }
  }

  const handlePrint = () => {
    const canvas = getPreviewCanvas()

    if (!canvas) return

    try {
      const pngDataUrl = canvas.toDataURL('image/png')

      // Print from an off-screen iframe rather than a popup: no visible window,
      // no popup blocker, and only the QR ends up on the page
      const iframe = document.createElement('iframe')
      iframe.setAttribute('aria-hidden', 'true')
      iframe.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
      document.body.appendChild(iframe)

      const frameWindow = iframe.contentWindow
      const frameDoc = frameWindow?.document

      if (!frameWindow || !frameDoc) {
        iframe.remove()
        message.error('Unable to print the QR code')
        return
      }

      let cleanedUp = false
      const cleanup = () => {
        if (cleanedUp) return
        cleanedUp = true
        iframe.remove()
      }

      frameDoc.write(`
        <html>
          <head><title>Loyalty QR code</title></head>
          <body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;">
            <img src="${pngDataUrl}" style="width:320px;height:320px" />
          </body>
        </html>
      `)
      frameDoc.close()

      const printImage = frameDoc.querySelector('img')

      const triggerPrint = () => {
        // remove the iframe once the print dialog closes (fires on cancel too);
        // a long fallback guards browsers that never fire onafterprint
        frameWindow.onafterprint = cleanup
        frameWindow.focus()
        frameWindow.print()
        setTimeout(cleanup, 60000)
      }

      // A data-URL image is usually decoded by the time it is in the document,
      // so print immediately; otherwise wait for its load event
      if (!printImage || printImage.complete) {
        triggerPrint()
      } else {
        printImage.onload = triggerPrint
      }
    } catch (error) {
      console.error('Unable to print the loyalty QR code', error)
      message.error('Unable to print the QR code')
    }
  }

  return (
    <>
      <Section>
        <SectionTitle>Loyalty QR code</SectionTitle>
        <SectionDescription>
          Manage your loyalty programme and in-store sign-up tools
        </SectionDescription>
        <Card>
          <QRPreview
            ref={qrPreviewRef}
            data-testid='loyalty-qr-code'
            data-url={walletPassQrUrl}
          >
            <QRCode
              value={walletPassQrUrl}
              size={QR_CANVAS_SIZE}
              level='M'
              includeMargin
            />
          </QRPreview>
          <Details>
            <UrlInput
              readOnly
              value={walletPassQrUrl}
              data-testid='loyalty-qr-url'
              suffix={
                <CopyIcon onClick={handleCopy} data-testid='loyalty-qr-copy' />
              }
            />
            <ButtonsRow>
              <Button
                type='ghost'
                className='-secondary'
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                data-testid='loyalty-qr-download'
              >
                Download
              </Button>
              <Button
                type='ghost'
                className='-secondary'
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                data-testid='loyalty-qr-print'
              >
                Print
              </Button>
              <Button
                type='ghost'
                className='-secondary'
                icon={<FullscreenOutlined />}
                onClick={() => setExpandVisible(true)}
                data-testid='loyalty-qr-expand'
              >
                Expand
              </Button>
            </ButtonsRow>
            <HelperText>
              Print and display in store. Customers scan to join your loyalty
              programme without the app
            </HelperText>
          </Details>
        </Card>
      </Section>

      <Modal
        open={expandVisible}
        closable={false}
        destroyOnClose
        onCancel={() => setExpandVisible(false)}
        footer={<Button onClick={() => setExpandVisible(false)}>Close</Button>}
      >
        <ExpandedQRWrapper data-testid='loyalty-qr-expand-modal'>
          <QRCode value={walletPassQrUrl} size={320} level='M' includeMargin />
        </ExpandedQRWrapper>
      </Modal>
    </>
  )
}

export default LoyaltyQRCard

const Section = styled.div`
  margin-top: 24px;
`

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  margin-bottom: 0;
`

const SectionDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin-bottom: 16px;
`

const Card = styled.div`
  display: flex;
  gap: 16px;
`

const QRPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 120px;
  height: 120px;

  /* qrcode.react renders at a high resolution for crisp download/print and
     sets an inline pixel size on the canvas; override the display size only */
  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`

const Details = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const UrlInput = styled(Input)`
  background: #f3f2f4;
  border: none;
  box-shadow: none;

  &:hover,
  &:focus,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none;
  }

  .ant-input {
    background: #f3f2f4;
  }
`

const CopyIcon = styled(CopyOutlined)`
  color: #4a4a68;
  cursor: pointer;
`

const ButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const HelperText = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin: 0;
`

const ExpandedQRWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`
